/**
 * Page identity for the recently-viewed producers (#1151, follow-up to #1138).
 *
 * A property's details rendering is NOT by itself a view of that property.
 * A layout or property widget pinned to a featured property — a manual
 * `data-hr-options='{"filter":{"property":"…"}}'` on the embed, or the
 * dashboard's Property select — renders those details on every load of a
 * NON-property page, and would keep that property at the top of the 10-slot
 * list forever. So producers record only when the PAGE is about the
 * property. Every signal below is a fact the page-building layers already
 * stamp; nothing here is a new setting. Any one is sufficient:
 *
 *  1. Property-page root. The renderer wraps every generated property page
 *     in `[data-hr-page="property-page-{feedId}"]`
 *     (`packages/homerunner-renderer/pages/api/render/[feedId]/property/[slug].tsx`,
 *     spliced into the host page by the worker inside `html`), and the embed
 *     loader's pdp role marks its root `[data-hr-pdp]`
 *     (`embed/loader-runtime.ts`). Both force `filter.property` onto every
 *     content widget inside, so "inside a property-page root" means "showing
 *     the page's own property". The walk crosses shadow boundaries: a layout
 *     slot child's host lives inside the layout's shadow root.
 *  2. URL. The last path segment of `location.pathname` equals the pushed
 *     slug: the reverse-proxy pdp route is `/<prefix>/[slug]`
 *     (`src/lib/homerunner/reverse-proxy-routes.ts`) and builder-hosted
 *     property pages use the same shape.
 *  3. Canonical. The last path segment of `<link rel="canonical">` equals
 *     the slug — the worker emits it for generated pages; CMS hosts whose
 *     URL is not the slug usually do too.
 *
 * Slugs compare as EXACT strings (Central slugs are lowercase and the worker
 * 301s paths to lowercase, #1131) after percent-decoding the URL segment.
 * Anything unreadable — no window, a throwing `decodeURIComponent`, a
 * malformed canonical — counts as "not the page's property": the failure
 * mode is a missed nicety, never a wrong entry.
 */
import { recordPropertyView } from './recently-viewed'

/** Roots stamped by the layers that build a property page (see header). */
export const PROPERTY_PAGE_ROOT_SELECTOR = '[data-hr-pdp], [data-hr-page^="property-page-"]'

/**
 * Last non-empty path segment, percent-decoded. `null` when there is none
 * (root path) or the segment does not decode.
 */
export function lastPathSegment(pathname: string): string | null {
  const trimmed = pathname.replace(/\/+$/, '')
  const last = trimmed.slice(trimmed.lastIndexOf('/') + 1)
  if (!last) return null
  try {
    return decodeURIComponent(last)
  } catch {
    return null
  }
}

/**
 * The page's own slug candidates, deduped: the URL path's last segment, then
 * the canonical link's. Empty outside a browser.
 */
export function readPageSlugs(
  doc: Pick<Document, 'querySelector'> | null = typeof document === 'undefined' ? null : document,
  loc: Pick<Location, 'pathname' | 'href'> | null = typeof location === 'undefined' ? null : location,
): string[] {
  const out: string[] = []
  const push = (slug: string | null) => {
    if (slug && !out.includes(slug)) out.push(slug)
  }
  if (loc) push(lastPathSegment(loc.pathname))
  const href = doc?.querySelector('link[rel="canonical"]')?.getAttribute('href')
  if (href) {
    try {
      push(lastPathSegment(new URL(href, loc?.href).pathname))
    } catch {
      // Malformed canonical — not a page-identity signal.
    }
  }
  return out
}

/**
 * Is `el` inside a property-page root? Walks up through `closest()` and, at
 * each shadow root, hops to its host and continues in the enclosing tree.
 */
export function isInsidePropertyPageRoot(el: Element | null | undefined): boolean {
  let node: Element | null = el ?? null
  while (node) {
    if (node.closest(PROPERTY_PAGE_ROOT_SELECTOR)) return true
    node = (node.getRootNode() as Partial<ShadowRoot>).host ?? null
  }
  return false
}

export type PagePropertyArgs = {
  /**
   * Slug candidates for the property being shown: the pushed/pinned
   * `filter.property` and, when the API returns it, the resolved slug.
   * Blanks and non-strings are ignored.
   */
  slugs: ReadonlyArray<string | null | undefined>
  /** The widget's host element (`useShadowHost()`); `null` skips signal 1. */
  host?: Element | null
  /** Test seams; default to the live document/location. */
  doc?: Pick<Document, 'querySelector'> | null
  loc?: Pick<Location, 'pathname' | 'href'> | null
}

/** The page-identity gate (see header): is this page about the property? */
export function isPagePropertyView({ slugs, host, doc, loc }: PagePropertyArgs): boolean {
  if (isInsidePropertyPageRoot(host)) return true
  const wanted = slugs.filter((s): s is string => typeof s === 'string').map((s) => s.trim()).filter(Boolean)
  if (wanted.length === 0) return false
  const pageSlugs = readPageSlugs(doc, loc)
  return wanted.some((slug) => pageSlugs.includes(slug))
}

/**
 * The producers' entry point: record a CONFIRMED view only when the page is
 * about the property. Returns whether the gate passed (the store still
 * validates the identifier itself — `recordPropertyView` is unchanged).
 */
export function recordPagePropertyView(centralPropertyUuid: string, args: PagePropertyArgs): boolean {
  if (!isPagePropertyView(args)) return false
  recordPropertyView(centralPropertyUuid)
  return true
}
